import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

export interface CallsPerDaySeries {
  labels: string[];
  data: number[];
}

export interface DistributionSeries {
  labels: string[];
  data: number[];
}

export interface CallStatsTotals {
  totalCalls: number;
  avgDurationSec: number;
  droppedCalls: number;
  completedCalls: number;
}

export interface CallStatsPayload {
  period: string;
  totals: CallStatsTotals;
  series: { callsPerDay: CallsPerDaySeries };
  outcomeDistribution: DistributionSeries;
  avgDurationByAgent: DistributionSeries; // labels: agent names, data: avg sec
}

export interface AgentPerformanceRow {
  id: string;
  name: string;
  totalCalls: number;
  avgDurationSec: number;
  successRate: number; // percentage 0..100
}

interface CallStatsState {
  period: string;
  stats: CallStatsPayload | null;
  agents: AgentPerformanceRow[];
  loading: boolean;
  agentsLoading: boolean;
  exportLoading: boolean;
  error: string | null;
}

const initialState: CallStatsState = {
  period: '7d',
  stats: null,
  agents: [],
  loading: false,
  agentsLoading: false,
  exportLoading: false,
  error: null,
};

export const fetchCallStats = createAsyncThunk<CallStatsPayload, void, { state: any }>(
  'callStats/fetchCallStats',
  async (_arg, thunkApi) => {
    const state: any = thunkApi.getState();
    const period: string = state.callStats?.period || '7d';
    const res = await api.get(`/analytics/call-stats?period=${encodeURIComponent(period)}`);
    return res.data as CallStatsPayload;
  }
);

export const fetchAgentStats = createAsyncThunk<{ period: string; agents: AgentPerformanceRow[] }, void, { state: any }>(
  'callStats/fetchAgentStats',
  async (_arg, thunkApi) => {
    const state: any = thunkApi.getState();
    const period: string = state.callStats?.period || '7d';
    const res = await api.get(`/analytics/call-stats/agents?period=${encodeURIComponent(period)}`);
    return res.data as { period: string; agents: AgentPerformanceRow[] };
  }
);

export const exportReport = createAsyncThunk<void, { format: 'csv' | 'pdf' }, { state: any }>(
  'callStats/exportReport',
  async ({ format }, thunkApi) => {
    const state: any = thunkApi.getState();
    const period: string = state.callStats?.period || '7d';
    const response = await api.get(`/analytics/call-stats/export?period=${encodeURIComponent(period)}&format=${encodeURIComponent(format)}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `call-stats-${period}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
);

const callStatsSlice = createSlice({
  name: 'callStats',
  initialState,
  reducers: {
    setPeriod: (state, action: PayloadAction<string>) => {
      state.period = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    update: (state, action: PayloadAction<Partial<CallStatsPayload>>) => {
      // Merge socket updates into stats
      state.stats = { ...(state.stats || { period: state.period, totals: { totalCalls: 0, avgDurationSec: 0, droppedCalls: 0, completedCalls: 0 }, series: { callsPerDay: { labels: [], data: [] } }, outcomeDistribution: { labels: [], data: [] }, avgDurationByAgent: { labels: [], data: [] } }), ...(action.payload as any) };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCallStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCallStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchCallStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load call stats';
      })
      .addCase(fetchAgentStats.pending, (state) => {
        state.agentsLoading = true;
        state.error = null;
      })
      .addCase(fetchAgentStats.fulfilled, (state, action) => {
        state.agentsLoading = false;
        state.agents = action.payload.agents || [];
      })
      .addCase(fetchAgentStats.rejected, (state, action) => {
        state.agentsLoading = false;
        state.error = action.error?.message || 'Failed to load agent stats';
      })
      .addCase(exportReport.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportReport.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportReport.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.error?.message || 'Failed to export report';
      });
  },
});

export const { setPeriod, clearError, update } = callStatsSlice.actions;
export default callStatsSlice.reducer;


